import { useTranslation } from "@sagot/i18n";
import {
  AppLink,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from "@sagot/ui";
import { reasonKey, type GapView } from "./gap-model";

export interface AssignableMember {
  userId: string;
  name: string;
}

export interface GapRowMenuProps {
  gap: GapView;
  /** Null when the viewer can't assign (no gaps.resolve / team.view). */
  members: readonly AssignableMember[] | null;
  canResolve: boolean;
  uploadHref: string | null;
  outdatedSourceHref: string | null;
  onAssign: (member: AssignableMember | null) => void;
  onIgnore: () => void;
}

/** Row "⋯" menu: upload / open outdated source, assign to member, ignore. */
export function GapRowMenu({ gap, members, canResolve, uploadHref, outdatedSourceHref, onAssign, onIgnore }: GapRowMenuProps) {
  const { t } = useTranslation();
  const outdated = reasonKey(gap.reason) === "outdated";
  if (!uploadHref && !outdatedSourceHref && !canResolve) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.gaps.more", { label: gap.label })}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {uploadHref ? (
          <DropdownMenuItem asChild>
            <AppLink href={uploadHref}>
              <Icon name="upload" className="size-4" />
              {outdated ? t("engage.gaps.uploadNewer") : t("engage.gaps.uploadDocument")}
            </AppLink>
          </DropdownMenuItem>
        ) : null}
        {outdatedSourceHref ? (
          <DropdownMenuItem asChild>
            <AppLink href={outdatedSourceHref}>
              <Icon name="file-text" className="size-4" />
              {t("engage.gaps.openOutdated")}
            </AppLink>
          </DropdownMenuItem>
        ) : null}
        {members && members.length > 0 ? (
          <>
            <DropdownMenuLabel>{t("engage.gaps.assignHeading")}</DropdownMenuLabel>
            {members.map((member) => (
              <DropdownMenuItem
                key={member.userId}
                onSelect={() => {
                  onAssign(member);
                }}
              >
                <Avatar name={member.name} className="size-6 text-[10px]" />
                {member.name}
                {gap.assignee_user_id === member.userId ? <Icon name="check" className="ml-auto size-4" /> : null}
              </DropdownMenuItem>
            ))}
            {gap.assignee_user_id ? (
              <DropdownMenuItem
                onSelect={() => {
                  onAssign(null);
                }}
              >
                <Icon name="x" className="size-4" />
                {t("engage.gaps.unassign")}
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}
        {canResolve ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onIgnore}>
              <Icon name="eye-off" className="size-4" />
              {t("engage.gaps.ignoreMenu")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
